// Formatting helpers shared by the Combat Log views.
//
// Everything the analyzer produces is a raw number, so one place decides how
// numbers, durations and timestamps are rendered. "No data" is spelled out
// instead of being faked with a zero, because an absent value and a zero value
// mean different things in a combat log.

const NUMBER = new Intl.NumberFormat('ru-RU')

export const NO_DATA = 'No data'

export function formatNumber(value) {
    return NUMBER.format(Math.round(Number(value) || 0))
}

export function formatCompact(value) {
    const number = Number(value) || 0
    const abs = Math.abs(number)
    if (abs >= 1000000000) return `${(number / 1000000000).toFixed(1)}B`
    if (abs >= 1000000) return `${(number / 1000000).toFixed(abs >= 10000000 ? 0 : 1)}M`
    if (abs >= 1000) return `${(number / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`
    return String(Math.round(number))
}

export function formatRate(value) {
    return `${formatCompact(value)}/s`
}

export function formatPercent(fraction, digits = 1) {
    const number = Number(fraction) || 0
    return `${(number * 100).toFixed(digits)}%`
}

// "1 death", "2 deaths". Only the regular plural is needed here; every noun the
// views count is a regular one.
export function plural(count, word) {
    const value = Number(count) || 0
    return `${value} ${word}${value === 1 ? '' : 's'}`
}

export function formatDuration(ms) {
    const total = Math.max(0, Math.round((Number(ms) || 0) / 1000))
    const hours = Math.floor(total / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const seconds = total % 60
    if (hours) return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    return `${minutes}:${String(seconds).padStart(2, '0')}`
}

export function formatClock(ts) {
    if (!ts) return '-'
    return new Date(ts).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export function formatDateTime(ts) {
    if (!ts) return '-'
    return new Date(ts).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
}

export function formatOffset(ms) {
    const sign = ms < 0 ? '-' : ''
    return `${sign}${formatDuration(Math.abs(ms))}`
}

export function formatSize(bytes) {
    const value = Number(bytes) || 0
    if (value >= 1073741824) return `${(value / 1073741824).toFixed(1)} GB`
    if (value >= 1048576) return `${(value / 1048576).toFixed(1)} MB`
    if (value >= 1024) return `${(value / 1024).toFixed(0)} KB`
    return `${value} B`
}

// The spec insists that every rate states which period it was divided by, so
// the label travels with the number.
export function describeBasis(report, basis) {
    if (!report) return ''
    if (basis === 'active') return 'per active combat time'
    if (report.basis === 'encounter') return 'per encounter duration'
    if (report.type === 'log') return 'per whole log duration'
    return 'per segment duration'
}

export function basisDurationMs(report, basis) {
    if (!report) return 0
    if (basis === 'active') return report.activeMs || 0
    return report.basisMs || report.durationMs || 0
}
